// SPDX-FileCopyrightText: 2026 Dusan Mijatovic (Netherlands eScience Center)
// SPDX-FileCopyrightText: 2026 Netherlands eScience Center
//
// SPDX-License-Identifier: Apache-2.0

'use client'

import {HomeProps} from '~/app/page'
import {useSession} from '~/auth/AuthProvider'
import MainContent from '~/components/layout/MainContent'
import ContentLoader from '~/components/layout/ContentLoader'
import useEposData from './useEposData'
import CounterBox from './CounterBox'
import Keywords from './Keywords'

import ImageAsBackground from '~/components/layout/ImageAsBackground'


export default function EposHome({counts}: HomeProps) {
  const {token} = useSession()
  const {loading, keywords} = useEposData(token)

  return (
    <MainContent className="flex-1 flex flex-col text-base-content bg-base-100">
      <>
        {/* Information banner */}
        <div className="max-w-(--breakpoint-xl) mx-auto p-4 grid lg:grid-cols-[1fr_1fr] gap-8 md:gap-10 lg:my-10 md:px-10">
          <div className="flex flex-col justify-center">
            <div className="mb-6">
              <h2 className="text-sm font-semibold text-base-content uppercase tracking-wider">EPOS TCS Tsunami</h2>
            </div>
            <h1 className="mt-4 text-4xl xl:text-5xl font-rsd-titles font-bold">
              EPOS TCS Tsunami 
            </h1>
            <div className="mt-8 text-lg text-base-content leading-relaxed">
              Welcome to the EPOS Thematic Core Services Tsunami's software directory. This platform aggregates software resources supporting tsunami research and coastal hazard management across the North-eastern Atlantic, Mediterranean, and connected seas.
            </div>
            <div className="mt-6 text-base-content">
              <p className="font-semibold mb-3">Our Focus:</p>
              <ul className="space-y-2 text-sm">
                <li><span className="font-medium">Tsunami Data Products</span> - Access to observation and modeling data</li>
                <li><span className="font-medium">Numerical Models</span> - Computational tools for tsunami simulation</li>
                <li><span className="font-medium">Hazard Products</span> - Risk assessment and management resources</li>
                <li><span className="font-medium">Community Support</span> - Connecting researchers and practitioners</li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col justify-center items-center lg:pl-8">
            <div className="bg-base-200 rounded-lg p-8 w-full">
              <h3 className="text-xl font-bold mb-6 text-base-content">Contributing Institutions</h3>
              <p className="text-sm text-base-content mb-4">
                A network of leading European research organizations dedicated to advancing tsunami science:
              <ImageAsBackground
                src="/images/EPOS-Geography.png"
                alt="EPOS TCS Tsunami Contributing Institutions"
                bgSize= 'contain' 
                className="w-full h-[20rem] sm:h-[25rem] md:h-[25rem] lg:h-[25rem]"
              />
              </p>
              <p className="text-xs text-base-content mt-6 italic">
                Learn more at <a href="https://www.tsunamidata.org" target="_blank" rel="noopener noreferrer" className="underline">tsunamidata.org</a>
              </p>
            </div>
          </div>
        </div>

        {/* Counters */}
        <div className="max-w-(--breakpoint-xl) mx-auto flex flex-wrap justify-between gap-10 md:gap-16 p-5 md:p-10">
          <h2 className="sr-only">Statistics</h2>
          <CounterBox
            label="Software Tools & Services"
            value={counts.software_cnt?.toString() ?? ''}
          />
          <CounterBox
            label="Research Citations"
            value={counts.software_mention_cnt?.toString() ?? ''}
          />
        </div>

        {/* Keywords */}
        {
          loading ?
            <ContentLoader />
            :
            <div className="mb-12">
              <Keywords keywords={keywords} />
            </div>
        }
      </>
    </MainContent>
  )
}
